<template>
  <div class="relationship-field">
    <label v-if="fieldLabel" class="form-field-label" :for="uniqueTitle + '-search'">{{ fieldLabel }}</label>
    <div v-if="selected.length || pendingLabel" class="relationship-selected">
      <span v-for="item in selected" :key="item.id" class="relationship-chip">
        {{ item.label }}
        <button type="button" class="relationship-chip-remove" @click="remove(item.id)" :aria-label="'Remove ' + item.label">×</button>
      </span>
      <span v-if="pendingLabel" class="relationship-chip">
        {{ pendingLabel }}
        <button type="button" class="relationship-chip-remove" aria-label="Remove the new record" @click="clearPending">×</button>
      </span>
    </div>
    <p v-if="pendingLabel" class="relationship-pending">Created and linked when you save this form.</p>
    <input
      :id="uniqueTitle + '-search'"
      v-model="query"
      type="search"
      class="form-control"
      :placeholder="placeholder || 'Search to link'"
      :aria-label="fieldLabel ? 'Search ' + fieldLabel : 'Search to link'"
      autocomplete="off"
      @focus="onFocus"
      @blur="onBlur"
      @input="scheduleLoad"
    />
    <div v-if="focused && options.length" class="relationship-options" role="listbox">
      <button
        v-for="option in options"
        :key="option.id"
        type="button"
        class="relationship-option"
        role="option"
        @mousedown.prevent
        @click="choose(option)"
      >{{ option.label }}</button>
    </div>
    <p v-else-if="focused && loading" class="text-muted relationship-status">Searching…</p>
    <p v-else-if="focused && loaded && query" class="text-muted relationship-status">No matches</p>
    <p v-else-if="focused && loaded" class="text-muted relationship-status">No choices</p>
    <p v-if="error" class="text-danger relationship-status">{{ error }}</p>
    <button v-if="allowAdd" type="button" class="btn btn-link px-0" @click="adding = !adding">{{ adding ? 'Cancel' : 'Add a record' }}</button>
    <div v-if="adding" class="relationship-add">
      <div v-for="field in addFields" :key="field.uniqueTitle" class="mb-2">
        <label class="form-field-label" :for="uniqueTitle + '-add-' + field.uniqueTitle">{{ field.title }}<span v-if="field.required"> *</span></label>
        <input :id="uniqueTitle + '-add-' + field.uniqueTitle" v-model="addValues[field.uniqueTitle]" class="form-control" type="text" />
      </div>
      <button type="button" class="btn btn-primary btn-sm" @click="submitAdd">Save and link</button>
    </div>
    <input type="hidden" :name="uniqueTitle" :value="serialized" />
  </div>
</template>

<script>
import axios from 'axios'
import { pageHostUniqueTitle } from '../../lib/page-host'

export default {
  name: 'FormRelationship',
  inject: {
    formValues: { default: () => ({}) },
    updateFormValue: { default: null },
    relationshipHost: { default: null }
  },
  props: {
    uniqueTitle: { type: String, default: '' },
    fieldLabel: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    description: { type: String, default: '' }
  },
  data () {
    return {
      query: '',
      options: [],
      selected: [],
      choice: 'single',
      allowAdd: false,
      parentFields: [],
      addFields: [],
      addValues: {},
      adding: false,
      pendingLabel: '',
      error: '',
      loaded: false,
      loadedSelection: false,
      focused: false,
      loading: false,
      timer: null,
      blurTimer: null
    }
  },
  computed: {
    serialized () {
      return JSON.stringify(this.selected.map((item) => item.id))
    },
    parentSignature () {
      return this.parentFields.map((field) => JSON.stringify(this.formValues && this.formValues[field])).join('|')
    },
    recordId () {
      return this.relationshipHost && this.relationshipHost.editRecordId
    }
  },
  watch: {
    parentSignature () {
      this.scheduleLoad(true)
    },
    recordId () {
      this.loadedSelection = false
      this.scheduleLoad(true)
    }
  },
  mounted () {
    this.scheduleLoad(true)
  },
  beforeUnmount () {
    if (this.timer) clearTimeout(this.timer)
    if (this.blurTimer) clearTimeout(this.blurTimer)
  },
  methods: {
    scheduleLoad (clearMismatch) {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(() => this.load(clearMismatch === true), 200)
    },
    optionsUrl (query) {
      const host = this.relationshipHost
      if (!host) return ''
      const page = pageHostUniqueTitle(host.$el)
      const params = new URLSearchParams()
      params.set('q', query || '')
      params.set('forLink', '1')
      if (this.recordId) params.set('recordId', String(this.recordId))
      params.set('row', JSON.stringify(this.formValues || {}))
      return `${VUE_APP_PROXY_SERVER_URL}/${host.routeAccount}/${host.$route.params.application}/pages/${page}/components/${host.uniqueTitle}/relationships/${this.uniqueTitle}/options?${params.toString()}`
    },
    onFocus () {
      if (this.blurTimer) clearTimeout(this.blurTimer)
      this.focused = true
      if (!this.loaded) this.scheduleLoad(true)
    },
    onBlur () {
      if (this.blurTimer) clearTimeout(this.blurTimer)
      this.blurTimer = setTimeout(() => {
        this.focused = false
      }, 150)
    },
    async load (clearMismatch) {
      const url = this.optionsUrl(this.query)
      if (!url) return
      this.loading = true
      try {
        const response = await axios.get(url)
        const payload = response.data || {}
        this.choice = payload.choice || 'single'
        this.allowAdd = !!payload.allowAdd
        this.parentFields = payload.parentFields || []
        this.addFields = payload.addFields || []
        this.options = payload.options || []
        if (!this.loadedSelection && this.recordId && Array.isArray(payload.selected)) {
          this.selected = payload.selected.slice()
          this.loadedSelection = true
          this.publish()
        }
        if (clearMismatch && !this.query) this.retain(this.options)
        this.loaded = true
        this.error = ''
      } catch (err) {
        this.options = []
        this.error = 'Could not load choices'
      } finally {
        this.loading = false
      }
    },
    retain (options) {
      const allowed = new Set((options || []).map((item) => Number(item.id)))
      const next = this.selected.filter((item) => allowed.has(Number(item.id)))
      if (next.length !== this.selected.length) {
        this.selected = next
        this.publish()
      }
    },
    publish () {
      if (this.updateFormValue) this.updateFormValue(this.uniqueTitle, this.selected.map((item) => item.id))
    },
    clearPending () {
      this.pendingLabel = ''
      const host = this.relationshipHost
      if (host && host.pendingRelationshipAdds) delete host.pendingRelationshipAdds[this.uniqueTitle]
    },
    choose (option) {
      const item = { id: option.id, label: option.label || option.displayValue || String(option.id) }
      if (this.choice === 'single') this.selected = [item]
      else if (!this.selected.some((current) => Number(current.id) === Number(item.id))) this.selected = this.selected.concat([item])
      this.clearPending()
      this.query = ''
      this.focused = this.choice !== 'single'
      this.publish()
      this.scheduleLoad(false)
    },
    remove (id) {
      this.selected = this.selected.filter((item) => Number(item.id) !== Number(id))
      this.publish()
    },
    async submitAdd () {
      this.error = ''
      const host = this.relationshipHost
      const values = { ...this.addValues }
      if (!this.recordId) {
        if (!host.pendingRelationshipAdds) host.pendingRelationshipAdds = {}
        host.pendingRelationshipAdds[this.uniqueTitle] = { values }
        const name = values[Object.keys(values)[0]] || 'New record'
        this.pendingLabel = String(name)
        this.adding = false
        this.addValues = {}
        if (this.choice === 'single') this.selected = []
        this.publish()
        return
      }
      try {
        const page = pageHostUniqueTitle(host.$el)
        const response = await axios.post(
          `${VUE_APP_PROXY_SERVER_URL}/${host.routeAccount}/${host.$route.params.application}/pages/${page}/components/${host.uniqueTitle}/relationships/${this.uniqueTitle}/add`,
          { recordId: this.recordId, values, row: this.formValues || {} }
        )
        const created = response.data || {}
        this.choose({ id: created.id, label: created.label || String(created.id) })
        this.adding = false
        this.addValues = {}
      } catch (err) {
        const data = err && err.response && err.response.data
        this.error = typeof data === 'string' && data.trim() ? data : 'Could not add that record'
      }
    }
  }
}
</script>

<style scoped>
.relationship-field { text-align: left; }
.relationship-selected { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 6px; }
.relationship-chip { display: inline-flex; align-items: center; gap: 4px; border: 1px solid #ccc; border-radius: 4px; padding: 2px 6px; }
.relationship-chip-remove { border: 0; background: transparent; cursor: pointer; }
.relationship-options { border: 1px solid #ddd; border-radius: 4px; margin-top: 4px; max-height: 160px; overflow: auto; }
.relationship-option { display: block; width: 100%; text-align: left; border: 0; background: transparent; padding: 6px 8px; cursor: pointer; }
.relationship-option:hover,
.relationship-option:focus { background: rgba(var(--bs-primary-rgb, 13, 110, 253), 0.12); }
.relationship-pending,
.relationship-status { margin: 4px 0 0; font-size: 0.875rem; text-align: left; }
.relationship-add { margin-top: 8px; }
</style>
